import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLazyQuery } from '@apollo/client';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import getSearchQuery from '../queries/search/getSearchQuery';
import { getProductQuery } from '../queries';
import { queryRequiresZipCode } from '../queries/product/getProductQuery';
import { getBatchedDomains } from '../react/util';
import DataProviderContext from '../react/DataProviderContext';
import { useQuery } from './useQuery';

const useSearch = ({
  keyword,
  storeId,
  zipCode,
  navParam,
  itemIds,
  channel,
  customerType,
  filter,
  storefilter,
  startIndex,
  pageSize,
  orderBy,
  additionalSearchParams,
  domains,
  productDomains,
  batchDomains = true,
  ssr = true,
  lazy = false,
  skip = false,
  lazyVariableListener = true,
}) => {

  const ctx = useContext(ExperienceContext);
  const dpContext = useContext(DataProviderContext);

  const sId = storeId || dpContext.storeIds || '8119';

  const dataSourcesContext = useContext(DataProviderContext);

  const batchedDomains = batchDomains && dataSourcesContext?.batchedDomains?.search
    ? getBatchedDomains(domains, dataSourcesContext.batchedDomains.search)
    : domains;

  let updatedProductDomains = [];
  if (productDomains && productDomains.length) {
    const productDomainsSet = new Set(productDomains);
    productDomainsSet.add('identifiers');
    updatedProductDomains = Array.from(productDomainsSet);
  } else if (batchedDomains.indexOf('products') > -1) {
    updatedProductDomains = ['identifiers'];
  }

  const batchedProductDomains = batchDomains && dataSourcesContext?.batchedDomains?.product
    ? getBatchedDomains(updatedProductDomains, dataSourcesContext.batchedDomains.product)
    : updatedProductDomains;

  const Query = getSearchQuery({
    domains: batchedDomains,
    productDomains: batchedProductDomains
  });

  const opts = {
    ssr,
    skip: skip || !(keyword || navParam || itemIds),
    // search model does not have an id and causes caching issues
    // so no cache after client loads
    // fetchPolicy: dataSourcesContext.mounted ? 'network-only' : 'cache-first',
    errorPolicy: 'all',
    variables: {
      // intentionally dont want null cause its different than undeifned for cache purposes
      storeId: sId
    },
    context: { timeout: 3000 }
  };

  if (startIndex) {
    opts.variables.startIndex = startIndex;
  }

  if (pageSize) {
    opts.variables.pageSize = pageSize;
  }

  if (orderBy) {
    opts.variables.orderBy = orderBy;
  }

  if (itemIds) {
    opts.variables.itemIds = itemIds;
  }

  if (filter && Object.keys(filter).length) {
    opts.variables.filter = filter;
  }

  if (storefilter) {
    opts.variables.storefilter = storefilter;
  }

  if (additionalSearchParams) {
    opts.variables.additionalSearchParams = additionalSearchParams;
  }

  if (customerType) {
    opts.variables.customerType = customerType;
  }

  if (channel) {
    opts.variables.channel = channel;
  }

  if (keyword) {
    opts.variables.keyword = keyword;
  }
  if (navParam) {
    opts.variables.navParam = navParam;
  }

  if (zipCode) {
    opts.variables.zipCode = zipCode;
  }

  let resp;
  let products;
  let searchReport;

  if (lazy) {

    if (lazyVariableListener) {
      return useLazyQuery(Query, opts);
    }

    const variables = { ...opts.variables };
    opts.variables = undefined;

    const [request, response] = useLazyQuery(Query, opts);

    products = response.data?.searchModel?.products || [];
    searchReport = response.data?.searchModel?.searchReport || {};

    resp = [request, response, variables];
  } else {
    resp = useQuery(Query, opts);
    products = resp.data?.searchModel?.products || [];
    searchReport = resp.data?.searchModel?.searchReport || {};
  }

  const searchResportString = JSON.stringify(searchReport);
  useEffect(() => {
    const respObj = lazy ? resp[1] : resp;
    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS && respObj.data && !respObj.loading) {
      const { data } = respObj;
      const { variables } = respObj;
      window.LIFE_CYCLE_EVENT_BUS.trigger('data-sources.data', {
        type: 'search',
        variables,
        data
      });
    }
    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS && respObj.error && !respObj.loading) {
      const { error } = respObj;
      const { variables } = respObj;
      window.LIFE_CYCLE_EVENT_BUS.trigger('data-sources.error', {
        type: 'search',
        variables,
        data: error
      });
    }
  }, [searchResportString]);

  if (dpContext.productCache) {
    products.forEach((product) => {
      dpContext.productCache.addProduct({
        product,
        storeId: lazy ? resp[1].variables.storeId : resp.variables.storeId
      });
    });
  }

  return resp;

};

useSearch.propTypes = {
  keyword: PropTypes.string,
  navParam: PropTypes.string,
  channel: PropTypes.oneOf(['DESKTOP', 'MOBILE', 'TABLET']),
  customerType: PropTypes.oneOf(['B2C', 'B2B']),
  filter: PropTypes.shape({
    rangefilter: PropTypes.string,
    lowerBound: PropTypes.number,
    upperBound: PropTypes.number
  }),
  startIndex: PropTypes.number,
  pageSize: PropTypes.number,
  orderBy: PropTypes.shape({
    field: PropTypes.oneOf([
      'TOP_SELLERS',
      'MOST_POPULAR',
      'TOP_RATED',
      'NEW_ITEMS',
      'BEST_MATCH',
      'PRODUCT_NAME',
      'BRAND_NAME',
      'PRICE'
    ]),
    order: PropTypes.oneOf([
      'ASC',
      'DESC'
    ])
  }),
  additionalSearchParams: PropTypes.shape({
    abfeatures: PropTypes.string,
    skipSpellCheck: PropTypes.bool
  }),
  storefilter: PropTypes.oneOf(['ALL', 'IN_STORE', 'ONLINE']),
  itemIds: PropTypes.arrayOf(PropTypes.string)
};

export default useSearch;